import { Divider, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import DraggableComponent from '../../DraggableComponent';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import DashboardDrawerTabDescription from '../../DashboardDrawerTabDescription';
import HelpPopover from '../../../HelpPopover';
import { parseRegisterCallableRegistrations } from '../../../../utils/pythonSourceScanUtils';
import { CUSTOM_CALLABLE_WIDGET_TYPE } from '../../../Graphs/sensor-utils';

const CustomDataContent = () => {
  const { t } = useTranslation();
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');

  const callableRegistrations = useMemo(() => parseRegisterCallableRegistrations(projectCode), [projectCode]);

  return (
    <Stack direction="column" width="100%" spacing={2} sx={{ p: 2 }} alignItems={'stretch'}>
      <Stack direction="row" spacing={0} alignItems={'center'} sx={{ width: '100%' }}>
        <DashboardDrawerTabDescription>
          <Trans i18nKey="dashboard.drawer.customData.description">
            Data streamed from custom registrations in your Python code. Drag widgets to the dashboard to visualize data when the program runs.
          </Trans>
        </DashboardDrawerTabDescription>
      </Stack>

      <Stack alignItems="center" sx={{ width: '100%' }}>
        <HelpPopover
          id="dashboard-custom-data-help-popover"
          triggerVariant="outlinedButton"
          triggerLabel={t('dashboard.drawer.customData.learnMore', 'Learn about custom data streams')}
          ariaLabel={t('dashboard.drawer.customData.learnMoreAriaLabel', 'Learn about custom data streams')}
          text={t(
            'dashboard.drawer.customData.help',
            'Register a getter with `DataStreamStore.get_instance().register_callable()` in your Python code to add custom streams here.'
          )}
        />
      </Stack>

      <Divider sx={{ width: '100%' }} />

      {callableRegistrations.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 4, px: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', width: '100%', fontSize: '0.75rem' }}>
            {t(
              'dashboard.drawer.customData.noCallables',
              'No custom streams detected. Register a callable with DataStreamStore.get_instance().register_callable() in your code to add custom data streams.'
            )}
          </Typography>
        </Stack>
      ) : (
        <Stack alignItems="center" spacing={1} sx={{ width: '100%' }}>
          {callableRegistrations.map((registration) => (
            <DraggableComponent
              key={`custom-callable-${registration.parameter}`}
              id={`custom-callable-${registration.parameter}`}
              type={CUSTOM_CALLABLE_WIDGET_TYPE}
              dataKey={registration.parameter}
              minW={4}
              title={registration.parameter}
              sx={{ m: 0 }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default CustomDataContent;
